import { portalText, usePortalTranslation, portalLocale } from "../../i18n/portal.js";
/**
 * The three business facts sign-up and "Finish setting up your business" both collect — design
 * §15.4.2 and contradiction C20 (`account.country` and `workspace.time_zone` are `NOT NULL`).
 *
 *   Business name   typed; it names the account and its first workspace.
 *   Country         CHOSEN by the person from the closed list in `@wetalk/domain`. The console
 *                   never proposes one: the select starts on "Choose a country", and nothing
 *                   can be submitted until the person has picked. Guessing from the browser's
 *                   locale would be exactly the default design §9.1 forbids.
 *   Time zone       PROPOSED from the browser (`resolvedOptions().timeZone`) and shown for
 *                   confirmation, with every zone the browser knows one click away.
 *
 * Country names come from `Intl.DisplayNames`, so the list holds codes only and no name can
 * drift from the browser's own spelling of it.
 */
import { Field, Input, Select } from "@wetalk/ui";
import { SUPPORTED_COUNTRY, isSupportedCountry, type SupportedCountry } from "@wetalk/domain";
import type { ReactNode } from "react";

/** The business facts as the form holds them: `country` is `null` until the person picks. */
export interface BusinessDraft {
  readonly accountName: string;
  readonly country: SupportedCountry | null;
  readonly timeZone: string;
}

/** The API's own limits (design §15.4.1 row 1), so the screen refuses before the round trip. */
export const BUSINESS_NAME_LENGTH = { minimum: 2, maximum: 120 } as const;

/** The browser's own zone — a proposal the person confirms, never a value sent unseen. */
export function proposedTimeZone(): string {
  return new Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** An empty draft, with the time zone proposed and the country deliberately unchosen. */
export function emptyBusinessDraft(): BusinessDraft {
  return { accountName: "", country: null, timeZone: proposedTimeZone() };
}

interface CountryOption {
  readonly code: SupportedCountry;
  readonly name: string;
}

function countryOptions(): readonly CountryOption[] {
  const names = new Intl.DisplayNames([portalLocale()], { type: "region" });
  return SUPPORTED_COUNTRY.map((code) => ({ code, name: names.of(code) ?? code })).sort(
    (left, right) => left.name.localeCompare(right.name, portalLocale()),
  );
}

/**
 * Every zone the browser knows, with the proposed one first when the list does not carry it
 * (a browser may resolve `UTC`, or a newer IANA spelling, that `supportedValuesOf` omits).
 */
function timeZoneOptions(selected: string): readonly string[] {
  const known = Intl.supportedValuesOf("timeZone");
  return known.includes(selected) ? known : [selected, ...known];
}

/** Why the draft cannot be sent yet, in the screen's words — or `null` when it can. */
export function businessDraftProblem(draft: BusinessDraft): string | null {
  const name = draft.accountName.trim();
  if (name.length < BUSINESS_NAME_LENGTH.minimum) {
    return portalText("core", "auth.tell_us_what_the_business_is_called_it_is_what_your_agent_will_sa_bde0f9");
  }
  if (name.length > BUSINESS_NAME_LENGTH.maximum) {
    return portalText("core", "auth.business_names_are_at_most_characters_6b2618", { value0: String(BUSINESS_NAME_LENGTH.maximum) });
  }
  if (draft.country === null) return portalText("core", "auth.choose_the_country_the_business_is_in_aa9bca");
  if (draft.timeZone === "") return portalText("core", "auth.choose_the_business_s_time_zone_6ba58d");
  return null;
}

export interface BusinessFieldsProps {
  readonly draft: BusinessDraft;
  readonly onChange: (draft: BusinessDraft) => void;
}

export function BusinessFields({ draft, onChange }: BusinessFieldsProps): ReactNode {
  usePortalTranslation("core");
  return (
    <>
      <Field label={portalText("core", "auth.business_name_a8e390")}>
        {(control) => (
          <Input
            {...control}
            name="accountName"
            value={draft.accountName}
            placeholder={portalText("core", "auth.roma_pizzeria_aba6bc")}
            autoComplete="organization"
            maxLength={BUSINESS_NAME_LENGTH.maximum}
            onChange={(event) => {
              onChange({ ...draft, accountName: event.currentTarget.value });
            }}
          />
        )}
      </Field>

      <Field label={portalText("core", "auth.country_d523eb")} description={portalText("core", "auth.where_the_business_is_it_decides_how_we_bill_and_comply_72f950")}>
        {(control) => (
          <Select
            {...control}
            name="country"
            value={draft.country ?? ""}
            onChange={(event) => {
              const chosen = event.currentTarget.value;
              onChange({ ...draft, country: isSupportedCountry(chosen) ? chosen : null });
            }}
          >
            <option value="" disabled>{portalText("core", "auth.choose_a_country_7843c8")}</option>
            {countryOptions().map((one) => (
              <option key={one.code} value={one.code}>
                {one.name}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Field
        label={portalText("core", "auth.time_zone_eea79a")}
        description={portalText("core", "auth.we_took_this_from_your_browser_change_it_if_the_business_keeps_an_bac7d6")}
      >
        {(control) => (
          <Select
            {...control}
            name="timeZone"
            value={draft.timeZone}
            onChange={(event) => {
              onChange({ ...draft, timeZone: event.currentTarget.value });
            }}
          >
            {timeZoneOptions(draft.timeZone).map((zone) => (
              <option key={zone} value={zone}>
                {zone.replaceAll("_", " ")}
              </option>
            ))}
          </Select>
        )}
      </Field>
    </>
  );
}
